import React, { useEffect, useState } from "react";
import { isUndefined } from "lodash";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { useNavigate } from "react-router-dom";
import StatusOrderChip from "@/app/global-components/StatusOrderChip";

import { useMediaQuery } from "react-responsive";
import TableComponent from "@/app/global-components/table";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ROLE } from "@/types";
import useOrder from "@/hooks/use-order";

interface Product {
  itemId: string;
  item: string;
  quantity: number;
  price: number;
}

interface Order {
  id: string;
  title: string;
  deliverDate: string;
  address: string;
  products: Product[];
  Sendedproducts: Product[];
  OrderNr: number;
  status: string;
  tender: { title: string };
  company: {
    nameAr: string;
    CompanyNr: string;
  };
}

const OrdersSList: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const navigate = useNavigate();
  const { fetchOrders } = useOrder();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const [listOrders, setListOrders] = useState<Order[]>([]);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;

  const [statuses] = useState([
    { label: t("status.planning"), name: "Planning", total: 0 },
    { label: t("status.opened"), name: "Opened", total: 0 },
    { label: t("status.closed"), name: "Closed", total: 0 },
    { label: t("status.accepted"), name: "Accepted", total: 0 },
    { label: t("status.payment"), name: "Payment", total: 0 },
    {
      label: t("status.paymentConfirmation"),
      name: "PaymentConfirmation",
      total: 0,
    },
    { label: t("status.sendingLabel"), name: "Sending", total: 0 },
    { label: t("status.receiving"), name: "Receiving", total: 0 },
    { label: t("status.finished"), name: "Finished", total: 0 },
    { label: t("status.canceled"), name: "Canceled", total: 0 },
  ]);

  useEffect(() => {
    const fetchOrdersData = async () => {
      try {
        if (myCompany?.id) {
          const result = await fetchOrders(myCompany?.id);
          setListOrders(result?.data || []);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
      }
    };
    fetchOrdersData();
  }, [myCompany?.id]);

  const calculateTotalPrice = (products: Product[]) =>
    products.reduce(
      (sum, product) => sum + product.price * product.quantity,
      0
    );

  const getStatusLabel = (status: string) => {
    const statusItem = statuses.find((s) => s.name === status);
    return statusItem ? statusItem.label : t("status.unknown"); // Default to t("status.unknown") if status not found
  };

  const handleNavigateToOrder = (orderId: string) => {
    navigate(`/Order/${orderId}`); // Navigate to the Order/id route
  };

  const columns = [
    {
      title: t("orders.title"),
      field: "title",
      render: (row: Order) => row.tender?.title || row.title,
    },
    {
      title: t("company.fields.address"),
      field: "address",
      render: (row: Order) => row.address || "N/A",
      hideOnMobile: true,
    },
    {
      title: t("orders.fields.totalPrice"),
      field: "totalPrice",
      render: (row: Order) =>
        `${calculateTotalPrice(row.products).toFixed(2)} ${t(
          "checkout.summary.currency"
        )}`,
    },
    {
      title: t("orders.fields.deliveryDate"),
      field: "deliverDate",
      render: (row: Order) => new Date(row.deliverDate).toLocaleDateString(),
    },
    {
      title: t("orders.fields.status"),
      field: "status",
      render: (row: Order) => (
        <StatusOrderChip status={getStatusLabel(row.status)} />
      ),
    },
  ];

  return (
    <div
      className="bg-white py-4 rounded-xl mx-4 md:px-10 relative"
      style={{ direction: "rtl" }}
    >
      <div className="text-2xl font-bold text-zinc-400 text-center">
        {t("orders.listTitle")}{" "}
        {userRole === ROLE.SELLER ? t("general.sales") : t("general.Purchases")}
      </div>
      <TableComponent
        columns={columns}
        data={listOrders}
        onRowClick={(row) => handleNavigateToOrder(row.id)}
        rowKeyField="id"
        maxHeight={isMobile ? 450 : 300}
        isMobile={isMobile}
      />
    </div>
  );
};

export default OrdersSList;
